import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { NgIf, NgStyle } from '@angular/common';
import { IvyFaceAnimatedComponent } from '../ivy-face-animated/ivy-face-animated.component';
import { ChatOption } from 'projects/chat-common/src/lib/models/chat.option';
import { OpenAIService } from '../../servicess/openai.service';
import { LoaderComponent } from '../loader/loader.component';
import { EventService } from 'projects/chat-common/src/lib/services/event.service';
import { Observable, Subject, Subscription, takeUntil } from 'rxjs';

@Component({
  selector: 'app-quick-actions-overlay',
  standalone: true,
  imports: [
    NgIf,
    IvyFaceAnimatedComponent,
    LoaderComponent,
    NgStyle,
  ],
  templateUrl: './quick-actions-overlay.component.html',
  styleUrl: './quick-actions-overlay.component.scss'
})
export class QuickActionsOverlayComponent implements OnInit {
  @Output()
  onQuickAction = new EventEmitter()

  @Output()
  closeQuickAction = new EventEmitter()

  @Output()
  addQuickAction = new EventEmitter<string>();

  @Output()
  selectedQuickAction = new EventEmitter<{ option: ChatOption, logOptions: { id: number, label: string, value: string, icon: string }[] | null } | null>();

  @Input()
  userData: { usageReason: string, age: number, hasPeriod: boolean, isPregnant: boolean, isPostpartum: boolean };

  @Input()
  showQuickActions = false;

  selectedOption: ChatOption | null;

  selectedSuboption: { option: ChatOption, icon: string, logOptions: { id: number, label: string, value: string, icon: string }[] | null, selectedOptions: { id: number, label: string, value: string, icon: string }[] | null, multi: boolean, color?: string };

  suboptions: { option: ChatOption, icon: string, logOptions: { id: number, label: string, value: string, icon: string }[] | null, selectedOptions: { id: number, label: string, value: string, icon: string }[] | null, multi: boolean, color?: string }[] = [];

  quickActionsCategories: ChatOption[] = [];

  showLoader: boolean = false;

  @Input() trigger: EventEmitter<void>;

  private destroy$ = new Subject<void>();
  private selectedSub?: Subscription;

  @Input() set selectedOptionTrigger(src$: Observable<ChatOption | null>) {
    this.selectedSub?.unsubscribe();
    if (src$) {
      this.selectedSub = src$
        .pipe(takeUntil(this.destroy$))
        .subscribe(o => this.selectQuickAction(o));
    }
  }

  constructor(
    private openaiService: OpenAIService,
    private eventService: EventService
  ) {}

  async ngOnInit() {
    this.quickActionsCategories = this.getQuickActionsCategories();
    this.selectedOption = this.quickActionsCategories[0];
    this.suboptions = await this.generateSubOptions();
    this.selectedSuboption = null;
    this.showLoader = false;

    this.trigger.subscribe(() => {
      this.goBackOrDismiss();
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.selectedSub?.unsubscribe();
  }

  get ivySize(): number {
    return window.innerWidth < 768 ? 92 : 140;
  }

  goBackOrDismiss() {
    if (this.selectedSuboption != null) {
      this.selectedSuboption = null;
      this.selectedQuickAction.emit(null);
      this.addQuickAction.emit('');
    } else {
      this.addQuickAction.emit('');
      this.selectedQuickAction.emit(null);
      this.closeQuickAction.emit();
    }
  }

  async selectQuickAction(option: ChatOption | null) {
    // On showQuickView, do nothing
    if (option.action == 'symptoms') {
      this.quickActionsCategories = this.getQuickActionsCategories();
      this.selectedOption = this.quickActionsCategories[0];
      this.suboptions = await this.generateSubOptions();
      let selectedSuboption = this.suboptions.find((o) => o.option.action == 'logSymptom');
      queueMicrotask(() => this.selectSuboption(selectedSuboption));
    }
  }

  selectLogOption(logOption: { id: number; label: string; value: string; icon: string; }) {
    const sub = this.selectedSuboption;
    if (!sub) return;

    const current = sub.selectedOptions ?? [];
    const exists = current.includes(logOption);

    if (sub.multi) {
      sub.selectedOptions = exists
        ? current.filter(v => v !== logOption)
        : [...current, logOption];
    } else {
      sub.selectedOptions = exists ? [] : [logOption];
    }

    let messageText = sub.selectedOptions.map((o) => o.label).join(', ');
    this.addQuickAction.emit(messageText);
    this.selectedQuickAction.emit({ option: this.selectedSuboption.option, logOptions: this.selectedSuboption.selectedOptions });
  }

  getQuickActionsCategories(): ChatOption[] {
    let defaultActions: ChatOption[] = [new ChatOption('myData', 'My data')]
    let usageReasonActions = [];
    switch (this.userData.usageReason) {
      case 'trackMyCycle':
        usageReasonActions = [new ChatOption('cycleSync', 'Cycle sync'), new ChatOption('fertility', 'Fertility'),]
        break;
      case 'toBecomePregnant':
        usageReasonActions = [new ChatOption('cycleSync', 'Cycle sync'), new ChatOption('fertility', 'Fertility'),]
        break;
      case 'toTrackCurrentPregnancy':
        usageReasonActions = [new ChatOption('healthyPregnancy', 'Healthy pregnancy'), new ChatOption('thisPregnancyWeek', 'This week'),]
        break;
      default:
        break;
    }

    defaultActions = [...defaultActions, ...usageReasonActions]
    defaultActions = [...defaultActions, new ChatOption('betterSleep', 'Better Sleep')]
    if (this.userData.age >= 18) {
      defaultActions.push(new ChatOption('betterSex', 'Better Sex'))
    }
    defaultActions = [...defaultActions, new ChatOption('nutrition', 'Nutrition'), new ChatOption('hotTopics', 'Hot topics')]
    return defaultActions;
  }

  selectSuboption(option: { option: ChatOption; icon: string; logOptions: { id: number, label: string, value: string, icon: string }[] | null, selectedOptions: { id: number, label: string, value: string, icon: string }[] | null, multi: boolean, color?: string }) {
    this.selectedSuboption = option;
    if (!option?.logOptions || option.logOptions.length === 0) {
      this.addQuickAction.emit(option.option.display);
    }
    this.selectedQuickAction.emit({ option: option.option, logOptions: option.selectedOptions });
  }

  async selectTabBarOption(option: ChatOption) {
    this.selectedOption = option;
    this.suboptions = await this.generateSubOptions();
    this.selectedSuboption = null;
    this.showLoader = false;
  }

  async generateSubOptions(): Promise<{ option: ChatOption; icon: string; logOptions: { id: number, label: string, value: string, icon: string }[] | null, selectedOptions: { id: number, label: string, value: string, icon: string }[] | null, multi: boolean, color?: string }[]> {
    switch (this.selectedOption.action) {
      case 'myData':
        return [
          { option: new ChatOption("logSymptom", "Symptoms"), icon: '❤️‍🩹', logOptions: this.eventService.getOptions("symptoms"), selectedOptions: [], multi: true, color: "#FEE0DA" },
          ...(!this.userData.hasPeriod && !this.userData.isPregnant
            ? [{ option: new ChatOption("startPeriod", "Period start"), icon: "🍎", logOptions: null, selectedOptions: null, multi: true }]
            : []),

          ...(this.userData.hasPeriod && !this.userData.isPregnant
            ? [{ option: new ChatOption("stopPeriod", "Period end"), icon: "🍏", logOptions: null, selectedOptions: null, multi: false }]
            : []),
          { option: new ChatOption("logFlow", "Flow"), icon: '🩸', logOptions: this.eventService.getOptions("flow"), selectedOptions: [], multi: false, color: "#FDF4D5" },
          { option: new ChatOption("logDischarge", "Discharge"), icon: '💧', logOptions: this.eventService.getOptions("discharge"), selectedOptions: [], multi: false, color: "#FFF0ED" },
          // { option: new ChatOption("logTemperature", "Basal temperature"), icon: '🌡️', logOptions: null, selectedOptions: null, multi: false },
          // { option: new ChatOption("logWeight", "Weight"), icon: '🌡️', logOptions: null, selectedOptions: null, multi: true },
          { option: new ChatOption("logMood", "Mood"), icon: '😊', logOptions: this.eventService.getOptions("moods"), selectedOptions: [], multi: true, color: "#FEE2EB" },
          { option: new ChatOption("logSex", "Sex"), icon: '💗', logOptions: this.eventService.getOptions("sex"), selectedOptions: [], multi: false, color: "#FFE5F8" },
          { option: new ChatOption("logDiet", "Unhealthy habits"), icon: '🍷', logOptions: this.eventService.getHabits(false), selectedOptions: [], multi: true, color: "#FDE8BF" },
          { option: new ChatOption("logDiet", "Healthy habits"), icon: '🥦', logOptions: this.eventService.getHabits(true), selectedOptions: [], multi: true, color: "#E2FFB4" },
          { option: new ChatOption("logSupplement", "Supplements"), icon: '💊', logOptions: this.eventService.getOptions("supplements"), selectedOptions: [], multi: true, color: "#CFF8E9" },
          { option: new ChatOption("logMedication", "Medication"), icon: '💊', logOptions: this.eventService.getOptions("medication"), selectedOptions: [], multi: true, color: "#D1F4FF" },
          ...(!this.userData.isPregnant
            ? [{ option: new ChatOption("logContraception", "Contraception"), icon: '💝', logOptions: this.eventService.getOptions("contraception"), selectedOptions: [], multi: true, color: "#ECE5F7" }]
            : []),
        ];
      case 'cycleSync':
        return [
          { option: new ChatOption("showTopics#{cycle,workout}", "Workout"), icon: '🤸‍♀️', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{cycle,mindfulness}", "Mindfulness"), icon: '🧘🏻‍♀️', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{cycle,supplements}", "Supplements"), icon: '💊', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{cycle,nutrition}", "Nutrition"), icon: '🥦', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{cycle,productivity}", "Productivity"), icon: '🎨', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{cycle,expert advice}", "Expert advice"), icon: '💡', logOptions: null, selectedOptions: null, multi: false }
        ];
      case 'betterSleep':
        return [
          { option: new ChatOption("showTopics#{better sleep,workout}", "Workout"), icon: '🤸‍♀️', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sleep,mindfullness}", "Mindfulness"), icon: '🧘🏻‍♀️', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sleep,supplements}", "Supplements"), icon: '💊', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sleep,nutrition}", "Nutrition"), icon: '🥦', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sleep,productivity}", "Productivity"), icon: '🎨', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sleep,expert advice}", "Expert advice"), icon: '💡', logOptions: null, selectedOptions: null, multi: false }
        ];
      case 'betterSex':
        return [
          { option: new ChatOption("showTopics#{better sex,workout}", "Workout"), icon: '🤸‍♀️', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sex,mindfulness}", "Mindfulness"), icon: '🧘🏻‍♀️', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sex,supplements}", "Supplements"), icon: '💊', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sex,nutrition}", "Nutrition"), icon: '🥦', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sex,productivity}", "Productivity"), icon: '🎨', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{better sex,expert advice}", "Expert advice"), icon: '💡', logOptions: null, selectedOptions: null, multi: false }
        ];
      case 'nutrition':
        return [
          { option: new ChatOption("showTopics#{nutrition,supplements}", "Supplements"), icon: '💊', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{nutrition,power ingredients", "Power ingredients"), icon: '🍓', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{nutrition,bowls}", "Bowls"), icon: '🥗', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{nutrition,smoothies}", "Smoothies"), icon: '🥤', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{nutrition,snacks}", "Snacks"), icon: '🍿', logOptions: null, selectedOptions: null, multi: false },
          { option: new ChatOption("showTopics#{nutrition,expert advice}", "Expert advice"), icon: '💡', logOptions: null, selectedOptions: null, multi: false }
        ];
      case 'fertility':
        this.showLoader = true;
        let fertilityTopics = await this.openaiService.requestFertilityHotTopics();
        return fertilityTopics.map(t => ({
          option: new ChatOption("showHotTopics", t),
          icon: '🌸',
          logOptions: null,
          selectedOptions: null,
          multi: false
        }));
      case 'healthyPregnancy':
        this.showLoader = true;
        let healthyPregnancyTopics = await this.openaiService.requestHealthyPregnancyTopics();
        return healthyPregnancyTopics.map(t => ({
          option: new ChatOption("showHotTopics", t),
          icon: '🤰',
          logOptions: null,
          selectedOptions: null,
          multi: false
        }));
      case 'thisPregnancyWeek':
        this.showLoader = true;
        let thisWeekTopics = await this.openaiService.requestHealthyPregnancyTopics();
        return thisWeekTopics.map(t => ({
          option: new ChatOption("showHotTopics", t),
          icon: '🤰',
          logOptions: null,
          selectedOptions: null,
          multi: false
        }));
      case 'hotTopics':
        this.showLoader = true;
        let hotTopics = await this.openaiService.requestHotTopics();
        return hotTopics.map(t => ({
          option: new ChatOption("showHotTopics", t),
          icon: '🔥',
          logOptions: null,
          selectedOptions: null,
          multi: false
        }));
      default:
        return [{ option: new ChatOption('', ''), icon: '', logOptions: null, selectedOptions: null, multi: false }];
    }
  }
}
